import React from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from "react-redux";
import QuestionTitle from "./QuestionTitle";
import ExplanationSection from "./ExplanationSection";
import QuestionOptionList from "./QuestionOptionList";
import QuizLoader from "./../Loader/QuizLoader";
import { captureAnswer } from "sdk/actions";
import QuestionDirection from "./QuestionDirection";
import useStyles from "sdk/custom-hooks/useStyles";
import LongMenu from "../../longMenu";
import { formatQuestion, TimeUtils } from "sdk/common";
import QuestionSolutionModal from '../QuestionSolutionModal/QuestionSolutionModal';
import { Box } from '@material-ui/core';

function changeTimeFormat(spentTime) {
    return TimeUtils.changeTimeFormat(spentTime);
}

/**
 * Single question box.
 * @param props
 * @returns {*}
 * @constructor
 */

function QuestionBox(props) {
    const dispatch = useDispatch();
    const { directionId, directionData, activeQuestionIndex, isLoading, mode, number, viewMode, total, drawerToggle, pauseQuiz, handleQuizPauseChange, setPauseQuiz } = props;
    const selectedQuestion = useSelector((state) => state.selectedQuestion);
    const language = useSelector((state) => state.user.language) || "en";
    const classes = useStyles(styles)();

    function onRadioChange(e, optionIndex, activeQuesIndex, quesType) {
        if (viewMode) {
            return;
        }
        let givenAns = selectedQuestion.givenAnswer || [];
        let clearRadio = false;
        if (quesType !== 'MultiChoice') {
            if (givenAns.includes(e.target.value)) {
                clearRadio = true;
            }
            givenAns = [];
        }

        if (!clearRadio) {
            if (e.target.checked) {
                givenAns.push(e.target.value);
            } else {
                givenAns = givenAns ? givenAns.filter((item) => {
                    return item !== e.target.value;
                }) : [];
            }
        }
        dispatch(captureAnswer(givenAns));
    }

    const {
        type,
        explanation,
        localesTitle,
        title,
        mark,
        penalty,
        givenAnswer,
        timeSpent,
        direction,
        options,
        markObtain,
        className,
        localesDirection
    } = formatQuestion(directionData, selectedQuestion, viewMode, language);
    const quizDirectionClass = direction?.type === 'LONG_TEXT' ? classes.quizDirectionBlock : '';

    return (
        <Box className={ drawerToggle ? classes.questionBoxExpand : classes.questionBox }>
          <Box className={classes.quizComprehension}>
            {direction?.name && direction?.type === "LONG_TEXT" ? (
              <QuestionDirection direction={direction} localesDirection={localesDirection} />
            ) : null}
            <Box className={classes.box + " " + quizDirectionClass}>
              {isLoading ? (
                <QuizLoader />
              ) : (
                <React.Fragment>
                  <QuestionTitle
                    mark={markObtain || "0"}
                    questionMark={mark}
                    directionData={directionData}
                    directionId={directionId}
                    type={type}
                    penalty={penalty}
                    pauseQuiz={pauseQuiz}
                    setPauseQuiz = {setPauseQuiz}
                    handleQuizPauseChange = {handleQuizPauseChange}
                    classNameInView={className || classes.marksBadges}
                    timeSpent={
                      timeSpent ? changeTimeFormat(timeSpent) : "00:00"
                    }
                    viewMode={viewMode}
                    direction={direction}
                    title={title}
                    total={total}
                    number={number}
                    localesTitle={localesTitle}
                    localesDirection={localesDirection}
                  />

                  <QuestionOptionList
                    options={options}
                    type={type}
                    showAnswer={viewMode}
                    viewMode={viewMode}
                    givenAnswer={givenAnswer}
                    mode={mode}
                    onRadioChange={onRadioChange}
                    activeQuestionIndex={activeQuestionIndex}
                  />

                  <Box className={classes.showAnswer}>
                    <LongMenu question={selectedQuestion} />
                  </Box>
                  <ExplanationSection explanation={explanation} viewMode={viewMode} mode={mode} showAnswer={viewMode} />
                </React.Fragment>
              )}
              <Box className={classes.solutionData}>
                  <QuestionSolutionModal question={selectedQuestion} />
              </Box>
            </Box>
          </Box>
        </Box>
    );
}

QuestionBox.propTypes = {
    activeQuestionIndex: PropTypes.number,
    directionData: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    directionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    drawerToggle: PropTypes.bool,
    handleQuizPauseChange: PropTypes.func,
    isLoading: PropTypes.bool,
    mode: PropTypes.string,
    number: PropTypes.number,
    pauseQuiz: PropTypes.bool,
    setPauseQuiz: PropTypes.func,
    total: PropTypes.number,
    viewMode: PropTypes.bool,
};

const styles = theme => ({
    questionBox: {
        position: 'fixed',
        top: 68,
        left: '17%',
        right: -5,
        maxHeight: 'calc(100vh - 150px)',
        transition: '0.3s all linear',
        overflow: 'hidden',
        '@media (max-width:767px)': {
            left: 0,
            right: 0,
            top: 55,
            maxHeight: 'calc(100vh - 0px)',

        },
        '@media (min-width: 768px) and (max-width:1023px)': {
            left: 0,
            right: 0,
            maxHeight: 'calc(100vh - 0px)',
        },
        '@media (min-width: 1024px) and (max-width:1024px)': {
            left: 0,
            right: 0,
            top: 70,
            position: "fixed",
            maxHeight: 'calc(100vh - 0px)',
        },
        '@media (min-width: 1025px) and (max-width:1439px)': {
            left: 330,
            right: 0,
            top: 70,
            position: "fixed",
            maxHeight: 'calc(100vh - 0px)',
        },
        '@media (min-width: 1440px) and (max-width:1799px)': {
            left: 0,
            right: 0,
            position: "fixed",
            maxHeight: 'calc(100vh - 0px)',
            margin: "0 0 0 23%"
        },
        '@media (min-width: 1800px) and (max-width:1850px)': {
            left: 0,
            right: 0,
            position: "fixed",
            maxHeight: 'calc(100vh - 0px)',
            margin: "0 0 0 18%"
        },
        '@media (min-width: 2350px) and (max-width:2560px)': {
            left: 0,
            right: 0,
            position: "fixed",
            maxHeight: 'calc(100vh - 0px)',
            margin: "0 0 0 13.5%"
        }
    },
    questionBoxExpand: {
        position: 'absolute',
        top: 85,
        left: -110,
        right: 20,
        maxHeight: 'calc(100vh - 150px)',
        transition: '0.3s all linear',
        overflow: 'hidden',
        '@media (min-width:280px) and (max-width:320px)': {
            left: 0,
            right: 0,
            top: 13,
            maxHeight: 'calc(100vh - 0px)'
        },
        '@media (min-width:321px) and (max-width:767px)': {
            left: 0,
            right: 0,
            top: 15,
            maxHeight: 'calc(100vh - 0px)'
        },
        '@media (min-width:768px) and (max-width:830px)': {
            left: 330,
            right: 0,
            top: 50,
            maxHeight: 'calc(100vh - 0px)',
            position: 'fixed'
        },
        '@media (min-width:831px) and (max-width:950px)': {
            left: 370,
            right: 0,
            top: 50,
            maxHeight: 'calc(100vh - 0px)',
            position: 'fixed'
        },
        '@media (min-width:951px) and (max-width:1024px)': {
            left: 320,
            right: 0,
            top: 70,
            maxHeight: 'calc(100vh - 0px)',
            position: 'fixed'
        },
        '@media (min-width:1025px)': {
            left: 0,
            right: 0,
            top: 62,
            position: 'fixed',
            maxHeight: 'calc(100vh - 0px)'
        },
    },
    solutionData: {
        textAlign: 'right',
        display: 'flex',
        float: 'right'
    },
    quizComprehension: {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        height: 'calc(100vh - 135px)',
        '@media (max-width:767px)': {
            height: '100vh'
        }
    },
    quizDirectionBlock: {
        maxHeight: 'calc(100vh - 145px)',
        overflow: 'auto'
    },
    box: {
        overflow: 'auto',
        maxHeight: 'calc(100vh - 145px)',
        float: 'left',
        width: 'calc(100% - 20px)',
        margin: '50px 25px 10px 15px',
        position: 'relative',
        textAlign: 'left',
        padding: 0,
        border: '1px solid #EAEAEA',
        backgroundColor: '#fff',
        '&::-webkit-scrollbar': {
            width: 6,
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#d3d5d3'
        },
        '&::-webkit-scrollbar-track': {
            backgroundColor: '#eeecec'
        },
        '@media (max-width:767px)': {
            width: 'calc(100% - 0px)',
            margin: 0,
            maxHeight: '100vh',
            padding: '0 11px',
            paddingBottom: 60
        }
    },
    showAnswer: {
        position: 'absolute',
        top: -5,
        right: 0,
        fontSize: 20,
        '@media (max-width:767px)': {
            top: 3,
            right: 0
        }
    },
    marksBadges: {
        backgroundColor: '#ffc65820',
        color: '#ffc658',
        border: '1px solid #ffc658',
        borderRadius: 2,
        padding: '2px 6px',
        fontSize: 13,
        textAlign: 'center',
        whiteSpace: 'nowrap',
        marginRight: 0,
        display: 'flex',
    }
});

export default QuestionBox;
